import { useState, useEffect, useRef, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { Cartesian3 } from 'cesium';
import CesiumMap from '../components/map/CesiumMap';
import MapErrorBoundary from '../components/common/MapErrorBoundary';
import TrackDetailPanel from '../components/map/TrackDetailPanel';
import AircraftDetailPanel from '../components/map/AircraftDetailPanel';
import SensorTooltip from '../components/map/SensorTooltip';
import { BasemapPicker } from '../components/map/basemaps';
import StatusRail from '../components/c2/StatusRail';
import RightRail from '../components/c2/RightRail';
import BottomDock from '../components/c2/BottomDock';
import CriticalAlertBanner from '../components/c2/CriticalAlertBanner';
import { useMapStore } from '../store/mapStore';
import { tracksApi, geofencesApi, missionsApi } from '../services/api';
import { format } from 'date-fns';
import {
  PlayIcon,
  PauseIcon,
  StopIcon,
  ForwardIcon,
  MapIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
  PlusIcon,
  MinusIcon,
  ChevronDoubleUpIcon,
  ChevronDoubleDownIcon,
  ArrowUturnLeftIcon,
  ArrowUturnRightIcon,
  HomeIcon,
  ChevronDownIcon,
  MapPinIcon,
} from '@heroicons/react/24/outline';
import clsx from 'clsx';

// Hook for press-and-hold continuous action
function usePressAndHold(action: () => void, intervalMs = 50) {
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const start = useCallback(() => {
    action(); // fire once immediately
    intervalRef.current = setInterval(action, intervalMs);
  }, [action, intervalMs]);

  const stop = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  return { onMouseDown: start, onMouseUp: stop, onMouseLeave: stop };
}

// Single nav button with press-and-hold
function NavButton({ icon: Icon, action, label }: {
  icon: React.ComponentType<{ className?: string }>;
  action: () => void;
  label: string;
}) {
  const handlers = usePressAndHold(action);
  return (
    <button
      {...handlers}
      title={label}
      className="p-2 rounded-sm bg-iron-700 hover:bg-iron-600 text-iron-300 hover:text-accent transition-colors"
    >
      <Icon className="h-4 w-4" />
    </button>
  );
}

export default function MapPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const replayParam = searchParams.get('replay');

  const {
    layers,
    toggleLayer,
    setLiveTrack,
    clearLiveTracks,
    setGeofences,
    setMissions,
    isPlaying,
    setPlayback,
    playbackTime,
    playbackSpeed,
    setPlaybackSpeed,
    setPlaybackTime,
    timeRange,
    replayTrackId,
    viewer,
    flyTo,
    basemapId,
    setBasemapId,
    setSelectedSensor,
  } = useMapStore();

  // Auto-zoom to user location on first load.
  //
  // The old version of this effect fired `flyTo()` from the geolocation
  // callback. The store's `flyTo` sets `flyToTarget` and clears it 100 ms
  // later — that window is too short if Cesium's CameraFlyTo hasn't been
  // mounted yet (or if the viewer is still booting), so the camera never
  // actually moved on a cold load. Switching to an imperative call against
  // the resolved `viewer.camera.flyTo` makes the zoom survive both a slow
  // geolocation prompt and a slow Cesium init.
  const hasAutoZoomed = useRef(false);
  useEffect(() => {
    if (hasAutoZoomed.current || replayParam) return;
    // Wait until the Cesium viewer is actually wired up — calling flyTo
    // before that means the camera command lands on a viewer that's about
    // to be replaced.
    if (!viewer) return;
    hasAutoZoomed.current = true;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        try {
          // Drive the camera imperatively so we don't depend on the store's
          // 100 ms-cleared flyToTarget flag — that timing was too tight on
          // cold loads.
          viewer.camera.flyTo({
            destination: Cartesian3.fromDegrees(
              pos.coords.longitude,
              pos.coords.latitude,
              50_000,
            ),
            duration: 1.5,
          });
        } catch {
          // Fall back to the store-driven flyTo if the direct call throws
          // (e.g. viewer torn down between resolve and execution).
          flyTo(pos.coords.longitude, pos.coords.latitude, 50_000);
        }
      },
      () => {}, // silently ignore if denied
      { enableHighAccuracy: false, timeout: 5_000 },
    );
  }, [flyTo, replayParam, viewer]);

  // Set replay track from URL param
  useEffect(() => {
    if (replayParam) {
      useMapStore.getState().setReplayTrackId(replayParam);
      useMapStore.getState().setLayers({ historicalTracks: true });
    }
    return () => {
      useMapStore.getState().setReplayTrackId(null);
    };
  }, [replayParam]);

  // Layer dropdown state
  const [layersOpen, setLayersOpen] = useState(false);

  // Playback speed options
  const speedOptions = [0.5, 1, 2, 5, 10] as const;

  // Fetch live tracks — poll every 2 seconds while not in replay mode.
  // We intentionally capture errors to surface them in the browser
  // console; the original code swallowed failures silently, making
  // "live tracks not showing" nearly impossible to diagnose.
  const { data: liveTracksData, error: liveTracksError } = useQuery({
    queryKey: ['liveTracks'],
    queryFn: () => tracksApi.getLiveTracks(),
    refetchInterval: isPlaying ? false : 2000,
    retry: 2,
  });

  // Log live track polling errors so they're visible during debugging
  useEffect(() => {
    if (liveTracksError) {
      console.error('[ASNE C2] Live tracks poll failed:', liveTracksError);
    }
  }, [liveTracksError]);

  // Fetch geofences
  const { data: geofencesData } = useQuery({
    queryKey: ['geofences'],
    queryFn: () => geofencesApi.list({ isActive: true }),
  });

  // Fetch missions
  const { data: missionsData } = useQuery({
    queryKey: ['missions', 'active'],
    queryFn: () => missionsApi.list({ status: 'ACTIVE' }),
  });

  // Update store with fetched data.
  // Clear the current-position map, then re-add from the API response.
  // Trail history persists across poll cycles so flight paths build up.
  // Trails for tracks that have gone stale (not in the API response) get
  // pruned here to prevent unbounded memory growth.
  useEffect(() => {
    if (liveTracksData?.data) {
      clearLiveTracks();
      const activeIds = new Set<string>();
      for (const track of liveTracksData.data) {
        setLiveTrack(track);
        activeIds.add(track.trackId);
      }
      // Prune trails for tracks that are no longer active
      const { liveTrackTrails } = useMapStore.getState();
      for (const trailId of liveTrackTrails.keys()) {
        if (!activeIds.has(trailId)) {
          liveTrackTrails.delete(trailId);
        }
      }
    }
  }, [liveTracksData, setLiveTrack, clearLiveTracks]);

  useEffect(() => {
    if (geofencesData?.data) {
      setGeofences(geofencesData.data);
    }
  }, [geofencesData, setGeofences]);

  useEffect(() => {
    if (missionsData?.data) {
      setMissions(missionsData.data);
    }
  }, [missionsData, setMissions]);

  // Layer toggles surfaced in the LAYERS dropdown. `historicalTracks`
  // and `heatmap` were removed from the UI — the underlying state in
  // mapStore defaults to false so the layers never render. They remain
  // in the store so the analytics page (which uses the heatmap as a
  // dedicated view) and the flight-replay flow (which sets historical
  // on programmatically when ?replay=… is in the URL) still function.
  const layerControls = [
    { key: 'liveTracks', label: 'Live Tracks' },
    { key: 'sensors', label: 'Sensors' },
    { key: 'geofences', label: 'Geofences' },
    { key: 'missions', label: 'Missions' },
    { key: 'facilities', label: 'FAA Facilities' },
    // Airspace restrictions (TFRs, Prohibited, Restricted, MOA). Fed by the
    // FAA TFR poll cron in the API service (Phase 4 item 20).
    { key: 'airspace', label: 'Airspace / TFR' },
    { key: 'openSky', label: 'ADS-B Aircraft' },
  ] as const;

  // Scene mode (Cesium 3D globe vs 2D Mercator). Persists in localStorage
  // so an operator's preference survives reload. We listen for changes and
  // call viewer.scene.mode imperatively because Cesium doesn't accept
  // sceneMode reactively.
  const [sceneMode, setSceneMode] = useState<'3D' | '2D'>(() => {
    if (typeof window === 'undefined') return '3D';
    return window.localStorage.getItem('c2.mapSceneMode') === '2D' ? '2D' : '3D';
  });
  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem('c2.mapSceneMode', sceneMode);
    if (!viewer) return;
    // morphTo2D / morphTo3D animate the transition (1s default). Cleaner
    // than slamming scene.mode directly and avoids flickering entities.
    if (sceneMode === '2D') viewer.scene.morphTo2D(1.0);
    else viewer.scene.morphTo3D(1.0);
  }, [sceneMode, viewer]);

  useEffect(() => {
    if (!layers.sensors) {
      setSelectedSensor(null);
    }
  }, [layers.sensors, setSelectedSensor]);

  return (
    <div className="h-full w-full flex flex-col bg-iron-900 overflow-hidden">
      {/* Critical alert banner — sits ABOVE the status rail. Renders nothing
          when no critical alerts are active, slides down dramatically when
          one fires. The single most prominent piece of UI when it matters. */}
      <CriticalAlertBanner />

      {/* C2 status rail — always visible at the top of the live map. */}
      <StatusRail mode={replayTrackId ? 'REPLAY' : 'LIVE'} />

      {/* Main row: map fills, RightRail sits on the right. */}
      <div className="flex-1 flex min-h-0 relative overflow-hidden">
        <div className="flex-1 relative overflow-hidden">
          {/* Map */}
          <MapErrorBoundary>
            <CesiumMap />
          </MapErrorBoundary>

      {/* Bottom detail panel for the currently-selected drone. Renders
          nothing when no track is selected. */}
      <TrackDetailPanel />

      {/* Aircraft (ADS-B) click popover. Renders nothing when no
          aircraft is selected. Slide-up from bottom-center, same
          slot as TrackDetailPanel. */}
      <AircraftDetailPanel />

      {/* Sensor click tooltip. Rendered as DOM text to avoid Cesium label
          speckle around sensor names/status text. */}
      <SensorTooltip />

      {/* Layer Controls — dropdown + basemap picker stacked */}
      <div className="absolute top-3 left-3 z-10 flex flex-col gap-2 items-start">
        <div className="relative">
          <button
            onClick={() => setLayersOpen(!layersOpen)}
            className="flex items-center gap-2 bg-iron-800/95 border border-iron-600 rounded-sm px-3 py-2 shadow-lg text-[12px] font-mono uppercase tracking-wider text-iron-100 hover:border-accent transition-colors"
          >
            <MapIcon className="h-4 w-4 text-accent" />
            LAYERS
            <span className="ml-1 bg-accent/20 text-accent text-[10px] font-mono font-semibold px-1.5 py-0.5">
              {layerControls.filter(({ key }) => layers[key]).length}
            </span>
            <ChevronDownIcon className={clsx('h-4 w-4 text-iron-400 transition-transform', layersOpen && 'rotate-180')} />
          </button>

          {layersOpen && (
            <>
              {/* Click-away backdrop */}
              <div className="fixed inset-0 z-0" onClick={() => setLayersOpen(false)} />
              <div className="absolute top-full left-0 mt-1 w-56 bg-iron-800/95 border border-iron-600 shadow-xl py-1 z-10">
                {layerControls.map(({ key, label }) => (
                  <button
                    key={key}
                    onClick={() => toggleLayer(key)}
                    className="flex items-center gap-2.5 w-full text-left px-3 py-1.5 text-[12px] transition-colors hover:bg-iron-700"
                  >
                    <div
                      className={clsx(
                        'w-3.5 h-3.5 border flex items-center justify-center transition-colors flex-shrink-0',
                        layers[key]
                          ? 'bg-accent border-accent'
                          : 'border-iron-500 bg-transparent'
                      )}
                    >
                      {layers[key] && (
                        <svg className="w-3 h-3 text-iron-900" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </div>
                    <span className={clsx(layers[key] ? 'text-iron-100' : 'text-iron-400')}>
                      {label}
                    </span>
                  </button>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Basemap picker — applies to every Cesium viewer in the app
            (live map, replay, historical, analytics heatmap). */}
        <div className="bg-iron-800/95 border border-iron-600 rounded-sm px-3 py-2 shadow-lg">
          <BasemapPicker value={basemapId} onChange={setBasemapId} label="BASEMAP" />
        </div>
      </div>

      {/* My Location + 2D/3D toggle. Same micro-pill control language as
          the LAYERS dropdown — sits in the top-right so the map fills
          everything between. */}
      <div className="absolute top-3 right-3 z-10 flex items-center gap-2">
        {/* 2D / 3D toggle. Segmented; click the inactive side to morph. */}
        <div className="inline-flex bg-iron-800/95 border border-iron-600 rounded-sm p-0.5 shadow-lg">
          {(['2D', '3D'] as const).map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => setSceneMode(m)}
              title={m === '2D' ? 'Switch to flat (2D) view' : 'Switch to globe (3D) view'}
              className={clsx(
                'px-2 py-1 text-[11px] font-mono font-semibold uppercase tracking-[0.15em] rounded-sm transition-colors',
                sceneMode === m
                  ? 'bg-accent text-iron-900'
                  : 'text-iron-300 hover:text-iron-100',
              )}
            >
              {m}
            </button>
          ))}
        </div>

        <button
          onClick={() => {
            navigator.geolocation.getCurrentPosition(
              (pos) => flyTo(pos.coords.longitude, pos.coords.latitude, 15000),
              (err) => console.warn('[Geolocation]', err.message),
              { enableHighAccuracy: true, timeout: 10000 },
            );
          }}
          title="Zoom to my location"
          className="bg-iron-800/95 border border-iron-600 hover:border-accent rounded-sm p-2 shadow-lg text-iron-400 hover:text-accent transition-colors"
        >
          <MapPinIcon className="h-4 w-4" />
        </button>
      </div>

      {/* Playback Controls (for flight replay) */}
      {!!replayTrackId && (
        <div className="absolute bottom-3 left-1/2 transform -translate-x-1/2 z-10 bg-iron-800/95 border border-iron-600 p-4 shadow-lg min-w-[500px]">
          {/* Track info line */}
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className="w-1.5 h-1.5 rounded-full bg-accent animate-pulse" />
              <span className="text-[10px] font-semibold uppercase tracking-[0.15em] text-iron-400">REPLAY</span>
              <span className="text-[11px] font-mono text-iron-100 truncate max-w-[200px]">
                {replayTrackId}
              </span>
            </div>
            <button
              onClick={() => {
                const newParams = new URLSearchParams(searchParams);
                newParams.delete('replay');
                setSearchParams(newParams);
                useMapStore.getState().setReplayTrackId(null);
                useMapStore.getState().setLayers({ historicalTracks: false });
              }}
              className="flex items-center gap-1 text-[11px] uppercase tracking-wider text-iron-400 hover:text-alert transition-colors"
            >
              <StopIcon className="h-3.5 w-3.5" />
              Stop
            </button>
          </div>

          {/* Controls row */}
          <div className="flex items-center gap-3">
            {/* Play/Pause button */}
            <button
              onClick={() => setPlayback(!isPlaying)}
              className="p-2.5 rounded-sm bg-accent hover:bg-accent-light text-iron-900 transition-colors flex-shrink-0"
            >
              {isPlaying ? (
                <PauseIcon className="h-5 w-5" />
              ) : (
                <PlayIcon className="h-5 w-5" />
              )}
            </button>

            {/* Timeline scrubber */}
            <div className="flex-1 flex flex-col gap-1">
              <input
                type="range"
                min="0"
                max="1000"
                value={
                  timeRange && playbackTime
                    ? Math.round(
                        ((playbackTime.getTime() - timeRange.start.getTime()) /
                          (timeRange.end.getTime() - timeRange.start.getTime())) *
                          1000,
                      )
                    : 0
                }
                onChange={(e) => {
                  if (timeRange) {
                    const progress = parseInt(e.target.value) / 1000;
                    const newTime = new Date(
                      timeRange.start.getTime() +
                        progress * (timeRange.end.getTime() - timeRange.start.getTime()),
                    );
                    setPlaybackTime(newTime);
                  }
                }}
                className="w-full h-1.5 bg-iron-600 rounded-sm appearance-none cursor-pointer accent-accent"
              />
              <div className="flex justify-between text-[10px] font-mono text-iron-400">
                <span>
                  {timeRange
                    ? format(timeRange.start, 'HH:mm:ss')
                    : '--:--:--'}
                </span>
                <span className="text-xs font-mono text-iron-100">
                  {playbackTime
                    ? format(playbackTime, 'HH:mm:ss')
                    : '--:--:--'}
                </span>
                <span>
                  {timeRange
                    ? format(timeRange.end, 'HH:mm:ss')
                    : '--:--:--'}
                </span>
              </div>
            </div>

            {/* Speed selector */}
            <div className="flex items-center gap-1 flex-shrink-0">
              <ForwardIcon className="h-3.5 w-3.5 text-iron-400" />
              {speedOptions.map((speed) => (
                <button
                  key={speed}
                  onClick={() => setPlaybackSpeed(speed)}
                  className={clsx(
                    'px-1.5 py-0.5 rounded-sm text-[10px] font-mono font-semibold transition-colors',
                    playbackSpeed === speed
                      ? 'bg-accent text-iron-900'
                      : 'bg-iron-700 text-iron-300 hover:bg-iron-600',
                  )}
                >
                  {speed}x
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ADS-B Altitude Legend */}
      {layers.openSky && (
        <div className="absolute bottom-3 left-3 z-10 bg-iron-800/95 border border-iron-600 px-3 py-2 shadow-lg">
          <p className="text-[10px] font-semibold text-iron-400 uppercase tracking-[0.15em] mb-1.5">ADS-B ALT</p>
          <div className="space-y-1">
            {[
              { color: '#c084fc', label: '> 25,000 ft', sub: 'En-route' },
              { color: '#5fd5ff', label: '10,000 – 25,000 ft', sub: 'Mid' },
              { color: '#ffb547', label: '< 10,000 ft', sub: 'Low' },
              { color: '#9aa7be', label: 'On ground', sub: '' },
            ].map((band) => (
              <div key={band.label} className="flex items-center gap-2">
                <div className="w-2.5 h-2.5 flex-shrink-0" style={{ backgroundColor: band.color }} />
                <span className="font-mono text-[11px] text-iron-200">{band.label}</span>
                {band.sub && <span className="text-[9px] uppercase text-iron-400">{band.sub}</span>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Camera Navigation Controls */}
      {viewer && (
        <div className="absolute bottom-3 right-3 z-10 bg-iron-800/95 border border-iron-600 p-1.5 shadow-lg flex items-center gap-1">
          {/* Pan */}
          <NavButton icon={ArrowLeftIcon} label="Pan left" action={() => {
            const amount = viewer.camera.positionCartographic.height * 0.1;
            viewer.camera.moveLeft(amount);
          }} />
          <div className="flex flex-col gap-1">
            <NavButton icon={ArrowUpIcon} label="Pan up" action={() => {
              const amount = viewer.camera.positionCartographic.height * 0.1;
              viewer.camera.moveUp(amount);
            }} />
            <NavButton icon={ArrowDownIcon} label="Pan down" action={() => {
              const amount = viewer.camera.positionCartographic.height * 0.1;
              viewer.camera.moveDown(amount);
            }} />
          </div>
          <NavButton icon={ArrowRightIcon} label="Pan right" action={() => {
            const amount = viewer.camera.positionCartographic.height * 0.1;
            viewer.camera.moveRight(amount);
          }} />

          <div className="w-px h-6 bg-iron-600 mx-1" />

          {/* Tilt */}
          <NavButton icon={ChevronDoubleUpIcon} label="Tilt up" action={() => {
            viewer.camera.lookUp(0.05);
          }} />
          <NavButton icon={ChevronDoubleDownIcon} label="Tilt down" action={() => {
            viewer.camera.lookDown(0.05);
          }} />

          <div className="w-px h-6 bg-iron-600 mx-1" />

          {/* Rotate */}
          <NavButton icon={ArrowUturnLeftIcon} label="Rotate left" action={() => {
            viewer.camera.twistLeft(0.05);
          }} />
          <NavButton icon={ArrowUturnRightIcon} label="Rotate right" action={() => {
            viewer.camera.twistRight(0.05);
          }} />

          <div className="w-px h-6 bg-iron-600 mx-1" />

          {/* Zoom */}
          <NavButton icon={PlusIcon} label="Zoom in" action={() => {
            const amount = viewer.camera.positionCartographic.height * 0.2;
            viewer.camera.zoomIn(amount);
          }} />
          <NavButton icon={MinusIcon} label="Zoom out" action={() => {
            const amount = viewer.camera.positionCartographic.height * 0.2;
            viewer.camera.zoomOut(amount);
          }} />

          <div className="w-px h-6 bg-iron-600 mx-1" />

          {/* Reset */}
          <NavButton icon={HomeIcon} label="Reset view" action={() => {
            viewer.camera.flyHome(1.5);
          }} />
        </div>
      )}
        </div>

        {/* C2 right rail — TRACKS / ALERTS / SENSORS tabs. Collapsible. */}
        <RightRail />
      </div>

      {/* C2 bottom dock — TIMELINE + LOG. Hidden when replay is active so
          the playback controls own the bottom-center slot. */}
      <BottomDock hidden={!!replayTrackId} />
    </div>
  );
}
